// src/components/GameAudioController.js
// Mounts at root level — handles the 3-phase audio lifecycle:
//   Phase 1: Startup sound fires on first user interaction
//   Phase 2: Login loop plays whenever user is on /auth
//   Phase 3: Stops everything when user logs in

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import GA from '../utils/GameAudio';

export default function GameAudioController() {
  const location = useLocation();
  const { user } = useAuth();
  const startupFiredRef  = useRef(false);
  const loginLoopOnRef   = useRef(false);
  const loggedInRef      = useRef(!!user);
  const unlockedRef      = useRef(false);

  const isAuthPage = location.pathname.startsWith('/auth');

  // ── Unlock AudioContext on any interaction ─────────────────
  useEffect(() => {
    const unlock = () => {
      if (unlockedRef.current) return;
      unlockedRef.current = true;
      GA.unlock();

      // Fire startup sound immediately on first interaction
      if (!startupFiredRef.current) {
        startupFiredRef.current = true;
        GA.playStartup();
      }
    };
    window.addEventListener('click',    unlock, { once: false });
    window.addEventListener('keydown',  unlock, { once: false });
    window.addEventListener('touchstart', unlock, { once: false });
    return () => {
      window.removeEventListener('click',     unlock);
      window.removeEventListener('keydown',   unlock);
      window.removeEventListener('touchstart',unlock);
    };
  }, []);

  // ── Also try to play startup sound as early as possible ───
  // Some browsers allow audio without interaction after a short delay
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!startupFiredRef.current) {
        startupFiredRef.current = true;
        GA.playStartup();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // ── Start/stop login loop based on route ──────────────────
  useEffect(() => {
    if (isAuthPage && !loginLoopOnRef.current && !loggedInRef.current) {
      loginLoopOnRef.current = true;
      // Slight delay so startup sound plays first
      setTimeout(() => {
        if (loginLoopOnRef.current) GA.startLoginLoop();
      }, 2600);
    }
  }, [isAuthPage]);

  // ── Stop everything when user successfully logs in ────────
  useEffect(() => {
    if (user && !loggedInRef.current) {
      loggedInRef.current = true;
      loginLoopOnRef.current = false;
      GA.stopAll(false); // smooth fade out
    }
  }, [user]);

  return null;
}
